<template>
  <div class="content terms-page">
    <div class="terms-shell">
      <div class="terms-header">
        <h1 class="page-title">약관/방침 관리</h1>
        <p class="page-sub">로케일 × 유형 매트릭스로 관리. 게시 시 S3에 자동 업로드되며 5분 내 캐시 갱신.</p>
      </div>

      <div class="toolbar">
        <div class="toolbar-l"></div>
        <div class="toolbar-r">
          <button class="btn" :disabled="isLoading" @click="fetchList">새로고침</button>
          <button class="btn btn-primary" @click="openCreateDialog">＋ 새 약관</button>
        </div>
      </div>

      <el-table
        class="terms-table"
        :data="sortedRows"
        v-loading="isLoading"
        :span-method="spanMethod"
        :row-class-name="rowClassName"
        :cell-class-name="cellClassName"
        :show-header="true"
      >
        <el-table-column label="언어" width="80">
          <template slot-scope="props">
            <div class="locale-cell-inner">
              <div class="locale-code">{{ props.row.locale }}</div>
              <div class="muted locale-sub">{{ localeLabel(props.row.locale) }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="유형" min-width="140">
          <template slot-scope="props">
            <span>{{ props.row.type }}</span>
            <span v-if="props.row.type === 'termsPolicies'" class="muted"> · 자동 합성</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="제목" min-width="180" />
        <el-table-column label="발행 상태" width="100" class-name="col-status">
          <template slot-scope="props">
            <span :class="['status', statusClass(props.row.status)]">{{ statusLabel[props.row.status] || props.row.status }}</span>
          </template>
        </el-table-column>
        <el-table-column label="현재 버전" width="110" align="right" class-name="col-version">
          <template slot-scope="props">
            <span class="num-cell">
              <span class="v-main">{{ props.row.currentVersionLabel || "v" + props.row.currentVersionNo }}</span>
              <span v-if="props.row.currentVersionLabel" class="v-sub">v{{ props.row.currentVersionNo }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="최근 발행일" width="170" class-name="col-datetime">
          <template slot-scope="props">
            <span class="muted">{{ formatTime(props.row.lastPublishedAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="임시저장 갱신일" width="170" class-name="col-datetime">
          <template slot-scope="props">
            <span class="muted">{{ formatTime(props.row.draftUpdatedAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="작업" width="140" align="right" class-name="actions-col col-actions">
          <template slot-scope="props">
            <template v-if="props.row.type === 'termsPolicies'">
              <span class="muted auto-tag">자동 생성</span>
              <button class="btn-text btn-sm" @click="goVersions(props.row)">버전</button>
            </template>
            <template v-else>
              <button class="btn-text btn-sm" @click="goEdit(props.row)">편집</button>
              <button class="btn-text btn-sm" @click="goVersions(props.row)">버전</button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog :visible.sync="createVisible" width="480px" title="새 약관 추가" :append-to-body="true" custom-class="terms-dialog" @close="resetCreateForm">
      <el-form label-position="top">
        <el-form-item label="언어 *">
          <el-select v-model="localeChoice" placeholder="locale 선택" class="w-100">
            <el-option v-for="opt in localeOptions" :key="opt.value" :value="opt.value" :label="opt.label" />
            <el-option key="__custom__" value="__custom__" label="직접 입력..." />
          </el-select>
          <el-input
            v-if="localeChoice === '__custom__'"
            v-model="customLocale"
            placeholder="예: zh, vi (2글자 lowercase)"
            style="margin-top: 8px"
            maxlength="2"
          />
        </el-form-item>
        <el-form-item label="유형 *">
          <el-select v-model="typeChoice" placeholder="type 선택" class="w-100">
            <el-option v-for="opt in typeOptions" :key="opt.value" :value="opt.value" :label="opt.label" />
            <el-option key="__custom__" value="__custom__" label="직접 입력..." />
          </el-select>
          <el-input
            v-if="typeChoice === '__custom__'"
            v-model="customType"
            placeholder="예: marketingConsent (camelCase, 영문/숫자/dash)"
            style="margin-top: 8px"
            maxlength="32"
          />
          <div class="dialog-hint">termsPolicies는 자동 합성되므로 직접 생성 안 됩니다.</div>
        </el-form-item>
        <el-form-item label="제목 (선택)">
          <el-input v-model="createForm.title" placeholder="예: 마케팅 정보 수신 동의" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <button class="btn" @click="createVisible = false">취소</button>
        <button class="btn btn-primary" :disabled="createSubmitting" @click="onCreateSubmit">생성</button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import { Table, TableColumn, Dialog, Form, FormItem, Select, Option, Input } from "element-ui";
import { formatDateTime } from "@/util/DateUtils";
import { showAPIErrorToast } from "@/util/ErrorUtils";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
  },
  async created() {
    await this.fetchList();
  },
  data() {
    return {
      isLoading: false,
      data: [],
      createVisible: false,
      createSubmitting: false,
      localeChoice: null,
      customLocale: "",
      typeChoice: null,
      customType: "",
      createForm: {
        title: null,
      },
      statusLabel: {
        NEVER_PUBLISHED: "미발행",
        PUBLISHED: "발행됨",
        PUBLISHING: "발행 중",
        FAILED: "실패",
      },
      localeOptions: [
        { value: "ko", label: "한국어 (ko)" },
        { value: "en", label: "English (en)" },
        { value: "ja", label: "日本語 (ja)" },
      ],
      // FR-052: termsPolicies 는 termsUse/privacyTerms (+ja 의 specifiedCommerce) publish 시
      // 자동 합성되므로 어드민이 직접 신규 생성하지 않는다. 신규 다이얼로그 select 에서 제거.
      typeOptions: [
        { value: "termsUse", label: "termsUse (이용약관)" },
        { value: "privacyTerms", label: "privacyTerms (개인정보 처리방침)" },
        { value: "specifiedCommerce", label: "specifiedCommerce (특정상거래법)" },
      ],
      localeNames: {
        ko: "한국어",
        en: "English",
        ja: "日本語",
      },
    };
  },
  computed: {
    sortedRows() {
      const localeOrder = { ko: 0, en: 1, ja: 2 };
      const typeOrder = { termsUse: 0, privacyTerms: 1, termsPolicies: 2, specifiedCommerce: 3 };
      return [...(this.data || [])].sort((a, b) => {
        const la = localeOrder[a.locale] ?? 99;
        const lb = localeOrder[b.locale] ?? 99;
        if (la !== lb) return la - lb;
        const ta = typeOrder[a.type] ?? 99;
        const tb = typeOrder[b.type] ?? 99;
        if (ta !== tb) return ta - tb;
        return (a.type || "").localeCompare(b.type || "");
      });
    },
    groupSpans() {
      const spanStart = {};
      const lastRow = new Set();
      const rows = this.sortedRows;
      let i = 0;
      while (i < rows.length) {
        const locale = rows[i].locale;
        let j = i;
        while (j < rows.length && rows[j].locale === locale) j++;
        spanStart[i] = j - i;
        lastRow.add(j - 1);
        i = j;
      }
      return { spanStart, lastRow };
    },
  },
  methods: {
    localeLabel(code) {
      return this.localeNames[code] || code;
    },
    statusClass(status) {
      switch (status) {
        case "PUBLISHED":
          return "published";
        case "NEVER_PUBLISHED":
          return "never-published";
        case "PUBLISHING":
          return "publishing";
        case "FAILED":
          return "failed";
        default:
          return "never-published";
      }
    },
    spanMethod({ rowIndex, columnIndex }) {
      if (columnIndex !== 0) return [1, 1];
      const span = this.groupSpans.spanStart[rowIndex];
      if (span) return [span, 1];
      return [0, 0];
    },
    rowClassName({ rowIndex }) {
      if (this.groupSpans.lastRow.has(rowIndex)) {
        return "locale-group-last";
      }
      return "";
    },
    cellClassName({ columnIndex, rowIndex }) {
      // 그룹 시작 row 의 locale 컬럼 (rowspan 시작 셀) 에 클래스 부여 →
      // 해당 td 의 border-bottom 이 rowspan 영역 마지막에 그려져 다른 컬럼과 연결됨
      if (columnIndex === 0 && this.groupSpans.spanStart[rowIndex]) {
        return "locale-cell locale-cell-group";
      }
      return "";
    },
    async fetchList() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get("/terms");
        this.data = res.data.data || res.data || [];
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isLoading = false;
      }
    },
    formatTime(value) {
      if (!value) return "—";
      return formatDateTime(value);
    },
    resetCreateForm() {
      this.localeChoice = null;
      this.customLocale = "";
      this.typeChoice = null;
      this.customType = "";
      this.createForm = { title: null };
    },
    openCreateDialog() {
      this.resetCreateForm();
      this.createVisible = true;
    },
    async onCreateSubmit() {
      const locale = this.localeChoice === "__custom__" ? (this.customLocale || "").trim() : this.localeChoice;
      const type = this.typeChoice === "__custom__" ? (this.customType || "").trim() : this.typeChoice;
      if (!locale) {
        window.Toast.error("locale을 선택해주세요");
        return;
      }
      if (!/^[a-z]{2}$/.test(locale)) {
        window.Toast.error("locale은 2글자 소문자여야 합니다");
        return;
      }
      if (!type) {
        window.Toast.error("type을 선택해주세요");
        return;
      }
      if (!/^[a-z][a-zA-Z0-9-]{0,31}$/.test(type)) {
        window.Toast.error("type은 영문 소문자로 시작, 영숫자+dash 조합, 최대 32자");
        return;
      }
      this.createSubmitting = true;
      try {
        const body = { locale, type };
        if (this.createForm.title) {
          body.title = this.createForm.title;
        }
        const res = await this.$axios.post("/terms", body);
        const created = res.data.data || res.data;
        // M4 — Navigate immediately. Skip fetchList() since the edit page
        // re-fetches the single document on mount; refreshing the list here
        // delays navigation without benefit.
        window.Toast.success("약관이 등록되었습니다");
        this.createVisible = false;
        this.resetCreateForm();
        if (created && created.id) {
          this.$router.push(`/terms/${created.id}/edit`);
        }
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.createSubmitting = false;
      }
    },
    goEdit(row) {
      this.$router.push(`/terms/${row.id}/edit`);
    },
    goVersions(row) {
      this.$router.push(`/terms/${row.id}/versions`);
    },
  },
};
</script>
<style scoped>
.w-100 {
  width: 100%;
}
/* .terms-page wrapper limits readability tweaks (font-size + line-height) to Terms pages.
   Other admin pages keep their existing font-size/line-height.
   Fluid scaling via clamp(): smooth growth from laptop (~1280px) to large monitor (1920px+). */
.terms-page {
  font-size: clamp(14px, 1vw, 19px);
  line-height: 1.6;
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.terms-shell {
  /* RETRY 19 — display:block prevents the parent flex layout from forcing
     terms-shell into a flex item that stretches its children (notably
     el-table) below the last row. */
  display: block;
  padding: 24px clamp(16px, 2.5vw, 64px);
  /* RETRY 19 — clamp the bottom padding (16-32px) instead of inheriting the
     full 24px+. The bottom whitespace below the table cannot exceed
     terms-shell's own padding-bottom now. */
  padding-bottom: clamp(16px, 1.5vw, 32px);
  max-width: clamp(960px, 95vw, 2400px);
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Apple SD Gothic Neo", sans-serif;
  color: var(--text-primary);
}
.terms-header {
  margin-bottom: 24px;
}
/* !important needed to override element-ui white-dashboard .content layer
   which forces a lighter text color on body content. */
.terms-page .page-title {
  font-size: clamp(20px, 1.8vw, 32px);
  font-weight: 700;
  color: var(--gray-9) !important;
  margin: 0 0 4px;
}
.terms-page .page-sub {
  font-size: clamp(13px, 1vw, 18px);
  color: var(--text-muted) !important;
  margin: 0;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}
.toolbar-l,
.toolbar-r {
  display: flex;
  gap: 8px;
}

/* Buttons (mockup look, scoped to this page) */
.btn {
  padding: 9px 18px;
  font-size: var(--font-size-base);
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid var(--gray-4);
  background: white;
  color: var(--text-secondary);
}
.btn:hover:not(:disabled) {
  border-color: var(--gray-5);
  color: var(--text-primary);
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}
.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  color: white;
}
/* Link-style text button — color/underline give clear affordance even before hover. */
.btn-text {
  background: transparent;
  border: none;
  color: var(--primary);
  padding: 6px 8px;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.btn-text:hover:not(:disabled) {
  color: var(--primary-hover);
  background: var(--primary-bg);
}
.btn-text.danger {
  color: var(--red);
}
.btn-text.danger:hover:not(:disabled) {
  color: var(--red-hover);
  background: var(--red-bg);
}
.btn-text:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}
.btn-sm {
  padding: 5px 10px;
  font-size: var(--font-size-sm);
}
/* Spacing between sibling .btn-text in actions column for clearer separation. */
.terms-page .actions-col .btn-text + .btn-text {
  margin-left: 4px;
}
/* termsPolicies row — '자동 생성' tag sits next to the [버전] button. */
.terms-page .actions-col .auto-tag {
  margin-right: 6px;
}
.terms-page .actions-col .auto-tag + .btn-text {
  margin-left: 0;
}

/* Status dot */
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  white-space: nowrap;
}
.status::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-5);
  display: inline-block;
}
.status.published::before {
  background: var(--green);
}
.status.never-published::before {
  background: var(--gray-5);
}
.status.publishing::before {
  background: var(--orange);
}
.status.failed::before {
  background: var(--red);
}
.status.failed {
  color: var(--red);
}

/* Cell helpers */
.locale-cell-inner {
  display: flex;
  flex-direction: column;
  font-weight: 500;
  color: var(--text-secondary);
}
.locale-code {
  font-size: var(--font-size-base);
}
.muted {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}
.num-cell {
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary);
}
/* Version display — label first (1.7), 정수 v숫자 보조 (v8). */
.v-main {
  font-weight: 600;
  color: var(--text-primary);
}
.v-sub {
  margin-left: 6px;
  color: var(--text-muted);
  font-size: clamp(11px, 0.7vw, 13px);
}
.dialog-hint {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 4px;
}
</style>
<style>
/* Unscoped overrides for el-table inside Terms list */
.terms-page .terms-table.el-table,
.terms-page .terms-table.el-table--fit {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  /* RETRY 19 — Override element-ui base `.el-table { flex: 1 }` (and the
     more-specific `.el-table--fit` rule) which causes the table to stretch
     and create an empty area below the last row when the parent is a flex
     container (white-dashboard layout). */
  flex: none !important;
  height: auto !important;
}
/* RETRY 19 — body-wrapper height reset: in some white-dashboard layouts the
   wrapper inherits a min-height/height that creates whitespace below the
   last row even when the outer table is flex:none. */
.terms-page .terms-table .el-table__body-wrapper {
  height: auto !important;
  min-height: 0 !important;
}
/* RETRY 19 — el-table renders a 1px ::before pseudo-line after the body that
   can read as an empty trailing line on certain monitors. Hide it. */
.terms-page .terms-table.el-table::before {
  display: none;
}
.terms-page .terms-table .el-table__header-wrapper th {
  background: var(--gray-1);
  color: var(--text-secondary);
  font-size: clamp(12px, 0.9vw, 16px) !important;
  font-weight: 500;
  border-bottom: 1px solid var(--gray-3);
  padding: 12px 0;
}
.terms-page .terms-table td {
  padding: 14px 0;
  font-size: clamp(13px, 1vw, 18px) !important;
  line-height: var(--line-height-base);
  color: var(--text-primary);
}
.terms-page .terms-table tr:hover > td {
  background: var(--gray-1) !important;
}
.terms-page .terms-table .locale-cell {
  background: var(--gray-1);
  border-right: 1px solid var(--gray-3);
}
.terms-page .terms-table .locale-group-last td {
  border-bottom: 2px solid var(--gray-5) !important;
}
.terms-page .terms-table td.locale-cell-group {
  border-bottom: 2px solid var(--gray-5) !important;
}
.terms-page .terms-table .actions-col .cell {
  text-align: right;
}
/* Cell wrap policy
   - 기본은 normal (긴 한글 유형/제목 줄바꿈 허용)
   - 한 단위로 표시되어야 하는 cell만 nowrap (datetime / status / version / actions) */
.terms-page .terms-table td .cell {
  white-space: normal;
  word-break: break-word;
}
.terms-page .terms-table td.col-datetime .cell,
.terms-page .terms-table td.col-status .cell,
.terms-page .terms-table td.col-version .cell,
.terms-page .terms-table td.col-actions .cell {
  white-space: nowrap;
}
.terms-page .terms-table td.col-datetime .cell {
  font-variant-numeric: tabular-nums;
}
/* Locale subtitle (한국어 / English / 日本語) — prevent wrap inside the narrow column. */
.terms-page .terms-table .locale-cell-inner .locale-sub {
  white-space: nowrap;
}
/* Reduce el-table empty-block min-height (default 60px) — prevents large gap below last row.
   When data is empty, "데이터 없음" 메시지는 cell padding으로 충분히 보임. */
.terms-page .terms-table .el-table__empty-block {
  min-height: 0;
}

/* New-terms dialog (mockup look) */
.terms-dialog .el-dialog__header {
  padding: 18px 24px;
  border-bottom: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body {
  padding: 24px;
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.terms-dialog .el-dialog__body .el-input__inner {
  font-size: var(--font-size-base);
}
.terms-dialog .el-dialog__footer {
  padding: 14px 24px;
  border-top: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__footer .btn + .btn {
  margin-left: 8px;
}
</style>
